import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { featuredComics, latestUpdates, allCategoryComics } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const allComics = [...featuredComics, ...latestUpdates, ...allCategoryComics];
  const comic = allComics.find((c) => c.slug === slug);

  if (!comic) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <i className="ri-emotion-sad-line text-5xl text-foreground-300 mb-3 block"></i>
            <p className="text-foreground-500">漫画未找到</p>
            <Link to="/" className="text-primary-500 text-sm mt-3 inline-block hover:text-primary-600">返回首页</Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: comic.title,
    description: comic.description,
    author: { '@type': 'Person', name: comic.author },
    datePublished: '2026-01-01',
  };

  const relatedComics = allComics.filter((c) => c.id !== comic.id && c.categorySlug === comic.categorySlug).slice(0, 3);

  return (
    <div className="min-h-screen bg-background-50">
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <Navbar />

      <div className="pt-16">
        <Breadcrumb items={[
          { label: '首页', href: '/' },
          { label: comic.category, href: `/category/${comic.categorySlug}` },
          { label: comic.title },
        ]} />
      </div>

      <main>
        <section className="relative w-full h-[300px] md:h-[380px] flex items-end overflow-hidden">
          <img src={comic.coverImage} alt={comic.title} className="absolute inset-0 w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20"></div>
          <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 lg:px-12 pb-8">
            <h1 className="text-3xl md:text-4xl font-heading text-background-50 mb-3 tracking-wide">{comic.title}</h1>
            <div className="flex flex-wrap items-center gap-4 text-background-50/80 text-sm">
              <span className="flex items-center gap-1"><i className="ri-user-line"></i>{comic.author}</span>
              <span className="flex items-center gap-1"><i className="ri-star-fill text-accent-400"></i>{comic.rating}</span>
              <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{comic.totalViews}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs ${comic.status === '连载中' ? 'bg-accent-500/80 text-foreground-950' : 'bg-foreground-500/50 text-background-50'}`}>
                {comic.status}
              </span>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
            <div className="flex flex-col lg:flex-row gap-10">
              <div className="lg:w-2/3">
                <h2 className="text-2xl font-heading text-foreground-900 mb-4">作品简介</h2>
                <p className="text-foreground-700 text-sm leading-relaxed mb-6">{comic.description}</p>

                <div className="flex flex-wrap gap-2 mb-8">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full">{tag}</span>
                  ))}
                </div>

                <div className="mb-10">
                  <h2 className="text-2xl font-heading text-foreground-900 mb-5">数据概览</h2>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-background-100 rounded-xl">
                      <p className="text-3xl font-heading text-primary-500">{comic.totalChapters}+</p>
                      <p className="text-foreground-500 text-xs mt-1">总章节数</p>
                    </div>
                    <div className="text-center p-4 bg-background-100 rounded-xl">
                      <p className="text-3xl font-heading text-primary-500">{comic.totalViews}</p>
                      <p className="text-foreground-500 text-xs mt-1">累计阅读</p>
                    </div>
                    <div className="text-center p-4 bg-background-100 rounded-xl">
                      <p className="text-3xl font-heading text-primary-500">{comic.rating}</p>
                      <p className="text-foreground-500 text-xs mt-1">用户评分</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:w-1/3">
                <div className="sticky top-24 bg-background-100 rounded-xl p-6">
                  <div className="aspect-[3/4] rounded-lg overflow-hidden mb-5">
                    <img src={comic.coverImage} alt={comic.title} className="w-full h-full object-cover" />
                  </div>
                  <Link
                    to={`/comic/${comic.slug}`}
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-medium rounded-full hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer text-sm"
                  >
                    开始阅读
                    <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {comic.chapters.length > 0 && (
          <section className="py-12 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
              <h2 className="text-2xl font-heading text-foreground-900 mb-6">章节列表</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {comic.chapters.map((ch) => (
                  <div key={ch.id} className="flex items-center justify-between p-4 bg-background-50 border border-background-200/70 rounded-lg hover:border-primary-200 transition-colors cursor-pointer">
                    <span className="text-sm text-foreground-700 font-medium">{ch.title}</span>
                    <span className="text-xs text-foreground-400">{ch.updatedAt}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {relatedComics.length > 0 && (
          <section className="py-12 md:py-16 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
              <h2 className="text-2xl font-heading text-foreground-900 mb-8">相关推荐</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
                {relatedComics.map((rc) => (
                  <Link key={rc.id} to={`/comic/${rc.slug}`} className="group relative aspect-[3/4] rounded-xl overflow-hidden cursor-pointer">
                    <img src={rc.coverImage} alt={rc.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent"></div>
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <h3 className="text-background-50 font-heading text-lg">{rc.title}</h3>
                      <p className="text-background-50/70 text-xs mt-1">{rc.author}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
}